import { Box, HStack } from '@chakra-ui/react'
import {
    loginSuccess,
    selectAuthEnvironment,
    selectIsAuthenticated,
    setDataCentre,
    setEnvironment,
    useDispatch,
    useKumodiLogin,
    useSelector
} from '@missionlabs/api'
import {
    Body,
    Button,
    Checkbox,
    Dropdown,
    ErrorMessage,
    FormProvider,
    Input,
    useYupForm,
    ValidatedInput,
    yup
} from '@missionlabs/react'
import { DeploymentDataCenters, DeploymentEnvironments } from '@missionlabs/types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useKeycloakLogin } from 'shared/auth/KeycloakProvider'
import {
    consumeStoredPostLoginRedirect,
    isKeycloakRequiredError
} from 'shared/auth/keycloakSession'
import { AuthLayout } from 'shared/components/AuthLayout'
import { DocumentTitle } from 'shared/components/DocumentTitle'
import { useAutoLogin } from 'shared/hooks/useAutoLogin'
import { useSetUpDestination } from 'shared/hooks/useSetUpDestination'
import { useLazyTempQuery, useLoginMutation } from 'shared/store'

interface LoginFields {
    username: string
    password: string
    rememberDetails: boolean
}

const loginSchema = yup.object({
    username: yup.string().required(),
    password: yup.string().required(),
    rememberDetails: yup.boolean()
})

export const Login = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const { t } = useTranslation()

    const isAuthenticated = useSelector(selectIsAuthenticated)
    const { dataCentre, environment } = useSelector(selectAuthEnvironment)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const autoLoginAttempted = useRef(false)

    const [handleAutoLogin, { session }] = useAutoLogin({
        redirectAfterLogin: location.state?.redirect ?? '/home',
        setErrorMessage
    })
    const setUpDestination = useSetUpDestination()

    const { loginWithKumodi, isLoading: isLoginLoading } = useKumodiLogin({
        useLazyTempQuery: useLazyTempQuery,
        useLoginMutation: useLoginMutation
    })
    const { loginWithKeycloak, error: keycloakError } = useKeycloakLogin()

    const methods = useYupForm<LoginFields>(loginSchema, {
        defaultValues: { username: '', password: '', rememberDetails: false }
    })
    const {
        formState: { isSubmitting, isValid }
    } = methods

    async function onSubmit(data: LoginFields) {
        const { username, password, rememberDetails } = data
        if (isLoginLoading) return

        setErrorMessage(null)

        try {
            const resp = await loginWithKumodi(username, password, rememberDetails)
            await setUpDestination(resp)
            dispatch(loginSuccess())
            return navigate(location.state?.redirect ?? '/home')
        } catch (e) {
            if (isKeycloakRequiredError(e)) {
                try {
                    await loginWithKeycloak(username)
                    return
                } catch (keycloakLoginError) {
                    console.error(keycloakLoginError)
                    setErrorMessage('Unable to start multi-factor sign in')
                    return
                }
            }
            console.error(e)
            setErrorMessage('data' in e ? e.data.message || e.data : e.message)
        }
    }

    useEffect(() => {
        if (keycloakError) setErrorMessage(keycloakError)
    }, [keycloakError])

    useEffect(() => {
        if (isAuthenticated) {
            return navigate(consumeStoredPostLoginRedirect() ?? location.state?.redirect ?? '/home')
        }
        if (!session || autoLoginAttempted.current) return

        autoLoginAttempted.current = true
        const { username } = session
        methods.reset({ username: username, password: username, rememberDetails: true })
        methods.handleSubmit(handleAutoLogin, console.error)()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session, isAuthenticated])

    return (
        <>
            <DocumentTitle breadcrumbs={['Login']} />
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(data => onSubmit(data))}>
                    <AuthLayout label={t('Sign in')}>
                        <ValidatedInput name="username" label={t('Email')} shouldDisable>
                            <Input autoComplete="username" placeholder={t('Email')} />
                        </ValidatedInput>
                        <ValidatedInput name="password" label={t('Password')} shouldDisable>
                            <Input
                                type="password"
                                autoComplete="current-password"
                                placeholder={t('Password')}
                            />
                        </ValidatedInput>
                        <ValidatedInput name="rememberDetails" shouldDisable>
                            <Checkbox name="rememberDetails" w="100%">
                                {t('remember-details')}
                            </Checkbox>
                        </ValidatedInput>

                        {errorMessage && (
                            <ErrorMessage
                                error={
                                    errorMessage === 'Unauthorized'
                                        ? t('Unauthorized')
                                        : errorMessage
                                }
                            />
                        )}

                        {__DEV__ && (
                            <HStack>
                                <ValidatedInput
                                    label="Data Centre"
                                    shouldDisable
                                    shouldRegister={false}
                                >
                                    <Dropdown
                                        options={Object.values(DeploymentDataCenters).map(v => ({
                                            label: v,
                                            value: v
                                        }))}
                                        value={dataCentre}
                                        onChange={value => dispatch(setDataCentre(value))}
                                    />
                                </ValidatedInput>
                                <ValidatedInput
                                    label="Environment"
                                    shouldDisable
                                    shouldRegister={false}
                                >
                                    <Dropdown
                                        options={Object.values(DeploymentEnvironments).map(v => ({
                                            label: v,
                                            value: v
                                        }))}
                                        value={environment}
                                        onChange={value => dispatch(setEnvironment(value))}
                                    />
                                </ValidatedInput>
                            </HStack>
                        )}
                        <Box w="full" pt="8px">
                            <Button
                                type="submit"
                                size="lg"
                                w="full"
                                isDisabled={!isValid}
                                isLoading={isSubmitting}
                            >
                                {t('Sign in')}
                            </Button>
                        </Box>
                        <Link to="/forgot-password">
                            <Body size="sm" variant="link-bold">
                                {t('Forgotten password?')}
                            </Body>
                        </Link>
                    </AuthLayout>
                </form>
            </FormProvider>
        </>
    )
}
